import http from './http';

import { createLogger } from '../utils/logger';

const logger = createLogger('crypto');
export interface CryptoInfoRec {
  id: string;
  userId: string;
  exchangeName?: string;
  walletType?: string;
  cryptocurrencyType?: string;
  walletAddress?: string;
  approximateValue?: number;
  purchaseDate?: string;
  purchasePrice?: number;
  storageLocation?: string;
  backupLocation?: string;
  notes?: string;
  isLocked?: boolean;
  username?: string;
  password?: string;
}

class CryptoInfoService {
  async createCryptoInfo(data: Partial<CryptoInfoRec>): Promise<CryptoInfoRec> {
    const response = await http.post('/api/crypto-info', data);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to create crypto information');
    }

    return response.data.data;
  }
  
  async getAllCryptoInfo(targetUserId?: string): Promise<CryptoInfoRec[]> {
    const params = targetUserId ? `?targetUserId=${targetUserId}` : '';
    const response = await http.get(`/api/crypto-info${params}`);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to fetch crypto information');
    }

    return response.data.data || [];
  }

  async getCryptoInfo(targetUserId?: string): Promise<CryptoInfoRec[]> {
    return this.getAllCryptoInfo(targetUserId);
  }

  async getCryptoInfoById(id: string): Promise<CryptoInfoRec> {
    const response = await http.get(`/api/crypto-info/${id}`);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to fetch crypto information');
    }

    return response.data.data;
  }
  
  async updateCryptoInfo(id: string, data: Partial<CryptoInfoRec>): Promise<CryptoInfoRec> {
    const response = await http.patch(`/api/crypto-info/${id}`, data);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to update crypto information');
    }

    return response.data.data;
  }
  
  async deleteCryptoInfo(id: string) {
    const response = await http.delete(`/api/crypto-info/${id}`);
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to delete crypto information');
    }
  }
  
  // Lock/Unlock functionality
  async lockCryptoInfo(id: string): Promise<void> {
    logger.debug('[Service Debug] Calling lock API for crypto ID:', id);
    const response = await http.post(`/api/crypto-info/${id}/lock`);
    logger.debug('[Service Debug] Lock API response:', response.data);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to lock crypto information');
    }
  }
  
  async unlockCryptoInfo(id: string, granteeEmails: string[]): Promise<void> {
    const response = await http.post(`/api/crypto-info/${id}/unlock`, { granteeEmails });
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to unlock crypto information');
    }
  }
  
  async getCryptoAccessGrants(id: string): Promise<any[]> {
    const response = await http.get(`/api/crypto-info/${id}/access-grants`);
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to fetch access grants');
    }
    
    return response.data.data || [];
  }
}

export const cryptoInfoService = new CryptoInfoService();